import { useState, useEffect, useCallback } from 'react';
import { Calendar } from './ui/calendar';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminCalendar = () => {
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);

  const fetchUnavailableDates = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/unavailable-dates`);
      setUnavailableDates(response.data.map(d => new Date(d.date)));
    } catch (error) {
      console.error('Error fetching dates:', error);
    }
  }, []);

  useEffect(() => {
    fetchUnavailableDates();
  }, [fetchUnavailableDates]);

  const handleDateSelect = (date) => {
    const dateStr = date.toDateString();
    if (selectedDates.find(d => d.toDateString() === dateStr)) {
      setSelectedDates(selectedDates.filter(d => d.toDateString() !== dateStr));
    } else {
      setSelectedDates([...selectedDates, date]);
    }
  };

  const markAsUnavailable = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/unavailable-dates`, {
        dates: selectedDates.map(d => d.toISOString())
      });
      toast.success('Dates marked as unavailable');
      setSelectedDates([]);
      fetchUnavailableDates();
    } catch (error) {
      toast.error('Failed to update dates');
    }
  };

  const removeUnavailableDate = async (date) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/unavailable-dates`, {
        data: { date: date.toISOString() }
      });
      toast.success('Date removed');
      fetchUnavailableDates();
    } catch (error) {
      toast.error('Failed to remove date');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-8">
      <Card>
        <CardHeader>
          <CardTitle>Manage Unavailable Dates</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold mb-2">Select dates to mark as unavailable:</h3>
              <Calendar
                mode="multiple"
                selected={selectedDates}
                onSelect={(dates) => setSelectedDates(dates || [])}
                className="rounded-md border"
              />
            </div>

            {selectedDates.length > 0 && (
              <Button onClick={markAsUnavailable} className="w-full">
                Mark {selectedDates.length} Date(s) as Unavailable
              </Button>
            )}

            <div>
              <h3 className="font-semibold mb-2">Currently Unavailable Dates:</h3>
              <div className="space-y-2">
                {unavailableDates.map((date, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-100 rounded">
                    <span>{date.toDateString()}</span>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => removeUnavailableDate(date)}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
